import { Loader2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type ProposalItem, useApproveProposal, useRejectProposal } from "@/lib/api";
import { errorDetail, errorStatus } from "@/lib/format";
import { logger } from "@/lib/logger";

function decisionError(err: unknown): string {
  switch (errorStatus(err)) {
    case 403:
      return "Only the page owner can decide on this proposal.";
    case 404:
      return "This proposal no longer exists.";
    case 503:
      return "GitHub is not reachable right now. Please try again in a moment.";
    default:
      return errorDetail(err) ?? "Something went wrong. Please try again.";
  }
}

interface DecisionDialogProps {
  proposal: ProposalItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDecided: (result: ProposalItem) => void;
}

export function ApproveDialog({ proposal, open, onOpenChange, onDecided }: DecisionDialogProps) {
  const approve = useApproveProposal();
  const [error, setError] = useState("");

  const handleApprove = async () => {
    setError("");
    logger.debug("[approvals] approving", proposal.id);
    try {
      const result = await approve.mutateAsync({ proposalId: proposal.id });
      logger.debug("[approvals] approve result", result?.status);
      if (result) {
        onDecided(result);
      }
      onOpenChange(false);
    } catch (err) {
      logger.warn("[approvals] approve failed", err);
      setError(decisionError(err));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setError("");
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve this change?</DialogTitle>
          <DialogDescription>
            The change to &quot;{proposal.title}&quot; will be merged into the wiki right away.
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-destructive text-sm">{error}</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApprove} disabled={approve.isPending}>
            {approve.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Approve and merge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RejectDialog({ proposal, open, onOpenChange, onDecided }: DecisionDialogProps) {
  const reject = useRejectProposal();
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  const handleReject = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = note.trim();
    if (!trimmed) {
      setError("Please add a short note so the proposer knows why.");
      return;
    }
    setError("");
    logger.debug("[approvals] rejecting", proposal.id);
    try {
      const result = await reject.mutateAsync({ proposalId: proposal.id, note: trimmed });
      if (result) {
        onDecided(result);
      }
      setNote("");
      onOpenChange(false);
    } catch (err) {
      logger.warn("[approvals] reject failed", err);
      setError(
        errorStatus(err) === 422
          ? "Please add a short note so the proposer knows why."
          : decisionError(err),
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setError("");
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <form onSubmit={handleReject} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Reject this change?</DialogTitle>
            <DialogDescription>
              The proposer will see your note. Keep it short and specific.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label htmlFor={`reject-note-${proposal.id}`} className="text-sm font-medium">
              Note
            </label>
            <textarea
              id={`reject-note-${proposal.id}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              required
              placeholder="For example: this pricing is out of date, see the Q3 sheet."
              className="border-input bg-card text-foreground placeholder:text-muted focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
            />
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={reject.isPending}>
              {reject.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Reject
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
